export type AgentFormat = "claude-code" | "codex" | "cursor" | "generic";

interface AgentInstructionsConfig {
  propertyId: string;
  baseUrl: string;
  apiKey?: string;
  siteDomain?: string;
}

function generateAgentInstructions(
  format: AgentFormat,
  config: AgentInstructionsConfig
): string {
  const { propertyId, baseUrl, apiKey, siteDomain } = config;
  const siteLabel = siteDomain ? ` for ${siteDomain}` : "";
  const keyHint = apiKey ? ` (yours starts with ${apiKey})` : "";

  const specUrl = "https://app.humblytics.com/agent.md";

  const setupSection = (() => {
    switch (format) {
      case "claude-code":
        return `### Setup: API key (do this once, before the first call)

Your API key is a secret. Do NOT paste it into CLAUDE.md, chat messages, or any file that gets committed to git.

1. Create a \`.env\` file in your project root${keyHint}:

   HUMBLYTICS_API_KEY=hmb_your_key_here

2. Add \`.env\` to \`.gitignore\` if it isn't already.

3. Load it into your shell before running Claude Code (or use a tool like direnv / dotenv):

   export $(grep -v '^#' .env | xargs)

4. From here on, always reference the key as \`$HUMBLYTICS_API_KEY\`. Never inline the literal value in commands you show the user or commit to the repo.

If \`$HUMBLYTICS_API_KEY\` is unset, stop and ask the user to run step 3. Do not ask them to paste the key into chat.`;
      case "cursor":
        return `### Setup: API key (do this once, before the first call)

Your API key is a secret. Do NOT paste it into .cursorrules, chat messages, or any file that gets committed to git.

1. Create a \`.env\` file in your project root${keyHint}:

   HUMBLYTICS_API_KEY=hmb_your_key_here

2. Add \`.env\` to \`.gitignore\` if it isn't already.

3. Load it into the terminal Cursor runs commands in (or use direnv / dotenv):

   export $(grep -v '^#' .env | xargs)

4. Always reference the key as \`$HUMBLYTICS_API_KEY\` in commands. Never inline the literal value.

If \`$HUMBLYTICS_API_KEY\` is unset, stop and ask the user to run step 3. Do not ask them to paste the key into chat.`;
      case "codex":
        return `### Setup: API key (do this once, before the first call)

Your API key is a secret. Do NOT paste it into AGENTS.md, chat messages, or any file that gets committed to git.

1. Create a \`.env\` file in your project root${keyHint}:

   HUMBLYTICS_API_KEY=hmb_your_key_here

2. Add \`.env\` to \`.gitignore\` if it isn't already.

3. Load it into the shell Codex runs commands in (or use direnv / dotenv):

   export $(grep -v '^#' .env | xargs)

4. Always reference the key as \`$HUMBLYTICS_API_KEY\` in commands. Never inline the literal value.

If \`$HUMBLYTICS_API_KEY\` is unset, stop and ask the user to run step 3. Do not ask them to paste the key into chat.`;
      case "generic":
      default:
        return `### Setup: API key (do this once, before the first call)

Your API key is a secret. Do NOT paste it into this system prompt, chat messages, or any file that gets committed to git.

1. Store the key in a \`.env\` file (or your platform's secret manager)${keyHint}:

   HUMBLYTICS_API_KEY=hmb_your_key_here

2. Add \`.env\` to \`.gitignore\`.

3. Make the variable available to the process that runs the agent (shell export, docker secret, hosted-env var, etc.).

4. Always reference the key as \`$HUMBLYTICS_API_KEY\`. Never inline the literal value.

If \`$HUMBLYTICS_API_KEY\` is unset, stop and ask the user to configure it. Do not ask them to paste the key into chat.`;
    }
  })();

  const core = `## Humblytics Analytics API${siteLabel}

You have access to the Humblytics Analytics API, a REST API for querying web analytics, running A/B tests, and building conversion funnels.

The full API spec is the best starting point. Fetch it first, then go from there based on what the user needs.

Full spec (all endpoints + params + examples):
curl -o HUMBLYTICS.md ${specUrl}

${setupSection}

### Quick Reference

API Base: ${baseUrl}
Auth header: Authorization: Bearer $HUMBLYTICS_API_KEY
Property ID: ${propertyId}

### Example call (safe pattern)

curl -H "Authorization: Bearer $HUMBLYTICS_API_KEY" \\
  "${baseUrl}properties/${propertyId}/traffic/summary?start=2025-01-01T00:00:00Z&end=2025-01-31T23:59:59Z&timezone=America/New_York"

Never substitute the literal key in place of \`$HUMBLYTICS_API_KEY\` when writing, logging, or echoing commands.

### Key Endpoints

GET /properties/${propertyId}/traffic/summary        - Aggregate traffic metrics
GET /properties/${propertyId}/traffic/trends          - Timeseries pageviews & visitors
GET /properties/${propertyId}/pages/breakdown         - All page performance
GET /properties/${propertyId}/clicks/breakdown        - Click data with top targets
GET /properties/${propertyId}/forms/breakdown         - Form submissions
GET /properties/${propertyId}/funnels                 - Run a funnel query
GET /properties/${propertyId}/split-test-recommendations?page=/path  - AI test suggestions
POST /properties/${propertyId}/split-tests            - Create an A/B test

### Query Parameters (most endpoints)

start     - ISO 8601 datetime (required)
end       - ISO 8601 datetime (required)
timezone  - IANA timezone, e.g. America/New_York (required)

### Rules

- Never ask the user to paste their API key into chat; reference \`$HUMBLYTICS_API_KEY\` from the environment
- Never write the literal key into a file, a commit, or a command you show the user
- If \`$HUMBLYTICS_API_KEY\` is missing, halt and point them back to the Setup section above
- Always fetch the full spec before making API calls
- All dates must be ISO 8601 format
- Timezone is required for accurate date bucketing
- The realtime endpoint does not require date parameters
- For split tests: get recommendations first, only test confidence >= 70`;

  switch (format) {
    case "claude-code":
      return `# Humblytics API

Add this to your CLAUDE.md to give Claude Code access to your analytics.

${core}`;

    case "cursor":
      return `# Humblytics API

Add this to your .cursorrules file.

${core}`;

    case "codex":
      return `# Humblytics API

Add this to your AGENTS.md file.

${core}`;

    case "generic":
    default:
      return `# Humblytics API

Add this to your agent's system prompt or context file.

${core}`;
  }
}

const SKILLS_REPO = "https://github.com/Humblytics/humblytics-marketing-skills.git";

const API_CONNECTED_SKILLS = "cro-optimizer, ab-test-generator, heatmap-analyst, revenue-attributor, funnel-reporter";
const CONTEXT_SKILLS = "page-cro, email-sequences, seo-strategist, marketing-strategist, copywriting, ad-expert, content-strategist";

/**
 * Intent-scoped copy: "Copy skills setup".
 *
 * Returns a natural-language instruction the user pastes into their coding
 * agent (Claude Code by default). The agent then installs the 12 Humblytics
 * marketing skills autonomously by cloning the repo and copying each skill
 * directory into the agent's skills folder, following the Agent Skills standard
 * (https://code.claude.com/docs/en/skills). Never inlines the literal API key.
 */
export function generateSkillsSetup(config: {
  propertyId: string;
  format?: AgentFormat;
}): string {
  const { propertyId, format = "claude-code" } = config;
  const propRef = propertyId || "<your-property-id>";

  // Where this agent discovers skills, per the Agent Skills standard.
  const target = (() => {
    switch (format) {
      case "cursor":
        return {
          assistant: "Cursor",
          skillsDir: ".cursor/skills",
          scope: "this project",
          restartNote:
            "If Cursor doesn't list the new skills yet, reload the window so it re-scans `.cursor/skills/`.",
        };
      case "codex":
        return {
          assistant: "Codex CLI",
          skillsDir: "~/.codex/skills",
          scope: "all my projects",
          restartNote:
            "If `~/.codex/skills/` did not exist before this session, restart Codex once so it starts watching the new directory.",
        };
      case "generic":
        return {
          assistant: "your agent",
          skillsDir: ".agent/skills",
          scope: "this project",
          restartNote:
            "If the skills don't show up, point your agent's config at the copied skills directory and reload it.",
        };
      case "claude-code":
      default:
        return {
          assistant: "Claude Code",
          skillsDir: "~/.claude/skills",
          scope: "all my projects",
          restartNote:
            "If `~/.claude/skills/` did not exist before this session, restart Claude Code once so it starts watching the new directory.",
        };
    }
  })();

  return `Set up the Humblytics marketing skills for me, end to end. Work autonomously using your shell, then tell me what you installed.

These are 12 open-source skills that follow the Agent Skills standard (https://code.claude.com/docs/en/skills). Each skill is a directory containing a SKILL.md. Install them so they are available in ${target.scope}.

Step 1. Clone the skills repo to a temp location:
  git clone ${SKILLS_REPO} /tmp/humblytics-marketing-skills

Step 2. Copy every skill directory into the skills folder, then clean up:
  mkdir -p ${target.skillsDir}
  cp -R /tmp/humblytics-marketing-skills/skills/* ${target.skillsDir}/
  rm -rf /tmp/humblytics-marketing-skills

  That installs 12 skills:
  - API-connected (these read live Humblytics data): ${API_CONNECTED_SKILLS}
  - Context-only (strategy and copy, no API needed): ${CONTEXT_SKILLS}

Step 3. Wire up credentials for the 5 API-connected skills. They read \`HUMBLYTICS_API_KEY\` from the environment, never from chat or a committed file.
  - In my current project, create a \`.env\` file with these lines:
      HUMBLYTICS_API_KEY=hmb_your_key_here
      HUMBLYTICS_PROPERTY_ID=${propRef}
  - Make sure \`.env\` is listed in \`.gitignore\`.
  - Do NOT ask me to paste my key into this chat and do NOT invent a value. Tell me to open \`.env\` and paste my key in myself, then run: source .env
  - From here on always reference the key as \`$HUMBLYTICS_API_KEY\`. Never write the literal value into a SKILL.md, a commit, or this conversation.

Step 4. Verify and report:
  - Confirm the new skills are discoverable (for example, the \`funnel-reporter\` and \`cro-optimizer\` skills should now be available).
  - ${target.restartNote}
  - Summarize which skills installed and remind me that the API-connected ones stay inactive until \`$HUMBLYTICS_API_KEY\` is set.`;
}

/**
 * OAuth-based MCP setup prompt. No API key required up front.
 * The LLM calls the authorize endpoint, directs the user to a URL,
 * polls for the key, then registers the MCP server.
 */
export function generateMcpOAuthSetupPrompt(
  format: AgentFormat,
  config: { mcpUrl: string; apiBaseUrl: string }
): string {
  const { mcpUrl, apiBaseUrl } = config;

  const registerStep = (() => {
    switch (format) {
      case "claude-code":
        return `Run this command (replace YOUR_API_KEY with the key from step 3):
   claude mcp add humblytics --transport http ${mcpUrl} --header "Authorization: Bearer YOUR_API_KEY"`;
      case "codex":
        return `Add to ~/.codex/config.toml (replace YOUR_API_KEY with the key from step 3):
   [mcp_servers.humblytics]
   url = "${mcpUrl}"
   http_headers = { "Authorization" = "Bearer YOUR_API_KEY" }`;
      case "cursor":
      case "generic":
      default:
        return `Register a remote MCP server (replace YOUR_API_KEY with the key from step 3):
   URL: ${mcpUrl}
   Header: Authorization: Bearer YOUR_API_KEY`;
    }
  })();

  return `Set up the Humblytics MCP server by following these steps:

1. Call this endpoint to start authorization (no auth required, no body):
   POST ${apiBaseUrl}/api/mcp/authorize
   It returns: { token, url }

2. Tell the user: "Please open this URL to connect your Humblytics account: <url from step 1>"
   Wait for them to confirm they have opened it.

3. Poll this endpoint every 2 seconds until status is "approved" (give up after 5 minutes):
   GET ${apiBaseUrl}/api/mcp/poll/<token from step 1>
   While pending, keep waiting and let the user know you're waiting for authorization.
   When approved, the response includes: { status: "approved", apiKey }

4. ${registerStep}

5. Verify the connection by calling the list_properties tool. Never print or log the API key.`;
}

/**
 * Intent-scoped copy: "Copy MCP setup prompt". Shown only in the moment the
 * full API key is visible (the create-key success dialog), so — unlike the
 * other generators here — this one interpolates the literal key and
 * propertyId rather than `$HUMBLYTICS_API_KEY`. The prompt itself instructs
 * the agent to never print or log the key back to the user.
 */
export function generateMcpSetupPrompt(
  format: AgentFormat,
  config: { mcpUrl: string; apiKey: string; propertyId: string }
): string {
  const { mcpUrl, apiKey, propertyId } = config;
  const propertyHeader = "X-Humblytics-Property-Id";
  const hasProperty = !!propertyId;

  const registerStep = (() => {
    switch (format) {
      case "claude-code":
        return `   - Claude Code: run
     claude mcp add humblytics --transport http ${mcpUrl} --header "Authorization: Bearer ${apiKey}"${hasProperty ? ` --header "${propertyHeader}: ${propertyId}"` : ""}`;
      case "codex":
        return `   - Codex CLI: add to ~/.codex/config.toml:
     [mcp_servers.humblytics]
     url = "${mcpUrl}"
     http_headers = { "Authorization" = "Bearer ${apiKey}"${hasProperty ? `, "${propertyHeader}" = "${propertyId}"` : ""} }`;
      case "cursor":
      case "generic":
      default:
        return `   - Any tool with remote/HTTP MCP support: use its remote/HTTP MCP config with this URL and headers:
     URL: ${mcpUrl}
     Header: Authorization: Bearer ${apiKey}${hasProperty ? `\n     Header: ${propertyHeader}: ${propertyId}` : ""}`;
    }
  })();

  const headersList = hasProperty
    ? `   - Authorization: Bearer ${apiKey}\n   - ${propertyHeader}: ${propertyId}`
    : `   - Authorization: Bearer ${apiKey}`;

  return `Set up the Humblytics MCP server for me:

1. Register a remote MCP server named "humblytics" at ${mcpUrl} (Streamable HTTP transport), sending these headers on every request:
${headersList}
${registerStep}
2. Verify the connection by listing the server's tools (expect ~36 Humblytics tools) and calling get_traffic_realtime with no arguments.
3. Never print or log my API key back to me.`;
}

/**
 * Intent-scoped copy: "Copy first test command".
 * Returns the safe env-var curl pattern, never a raw literal key.
 */
export function generateFirstTestCommand(config: {
  propertyId: string;
  baseUrl: string;
  useEnvVar?: boolean;
}): string {
  const { propertyId, baseUrl, useEnvVar = true } = config;
  const keyRef = useEnvVar ? "$HUMBLYTICS_API_KEY" : "hmb_your_key_here";
  const propRef = useEnvVar ? "$HUMBLYTICS_PROPERTY_ID" : propertyId || "your_property_id";
  return `curl -H "Authorization: Bearer ${keyRef}" \\
  "${baseUrl}properties/${propRef}/traffic/summary?start=2025-01-01T00:00:00Z&end=2025-01-31T23:59:59Z&timezone=America/New_York"`;
}

/**
 * How the agent installs the script: through a site builder's own MCP server
 * (Webflow, Framer), or by editing the site's code (GitHub repo, or a project
 * open in Cursor, Claude Code or Codex).
 */
export type ScriptInstallBuilder = "webflow" | "framer" | "github";

/**
 * Official remote MCP servers, checked against the vendors' docs on
 * 2026-10-28 (developers.webflow.com/mcp/reference/getting-started,
 * framer.com/help/articles/getting-started-with-framer-mcp).
 */
const BUILDER_MCP: Record<Exclude<ScriptInstallBuilder, "github">, { name: string; url: string; docs: string; where: string }> = {
  webflow: {
    name: "Webflow",
    url: "https://mcp.webflow.com/mcp",
    docs: "https://developers.webflow.com/mcp/reference/getting-started",
    where:
      "Add it as site-wide custom code in the <head> (register it as an inline site script, then apply it to the site), then publish the site.",
  },
  framer: {
    name: "Framer",
    url: "https://mcp.framer.com/mcp",
    docs: "https://www.framer.com/help/articles/getting-started-with-framer-mcp/",
    where: "Add it to the project's site settings, custom code, start of <head>, then publish the project.",
  },
};

/**
 * Copy for "Your agent adds the script": a self-contained prompt. It carries
 * the literal snippet (public, unlike an API key), the domain to check, and
 * the rules: add, never replace; one change only; no guessing. For Webflow and
 * Framer it uses the builder's MCP, or tells the person how to connect it and
 * stops. For code it edits the site's root <head> and opens a pull request.
 */
export function generateScriptInstallPrompt(
  builder: ScriptInstallBuilder,
  config: { snippet: string; domain: string },
): string {
  const site = config.domain || "my site";
  const rules = `Rules: add this one script and change nothing else. Keep any code that is already there; never replace it. If anything is unclear, ask me instead of guessing.`;

  if (builder === "github") {
    return `Add the Humblytics tracking script to my site (${site}). Its code is in a repo.

1. Find the code. If the site's project is open in your workspace (Cursor, Claude Code, Codex), use it. Otherwise use your GitHub tools (the GitHub MCP or the gh CLI). If you can reach neither, tell me which repo you need and stop.
2. Find the file that renders the <head> on every page, for example app/layout.tsx, pages/_document.tsx, index.html, a base template or theme.liquid. Add this inside <head>:

${config.snippet}

3. Make the change on a new branch and open a pull request. Don't merge it; send me the link so I can.
4. When it's merged and deployed, open ${site} once so Humblytics sees the first visit, then tell me it's done.

${rules}`;
  }

  const { name, url, docs, where } = BUILDER_MCP[builder];
  return `Add the Humblytics tracking script to my ${name} site (${site}).

1. Check that you have ${name} MCP tools. If you don't, stop and tell me to connect the official ${name} MCP server: add the remote server ${url} in your MCP settings, sign in to ${name} and approve. Setup guide: ${docs}. Then I'll ask you again.
2. With the ${name} MCP tools: ${where} Add this, don't replace anything:

${config.snippet}

3. When it's published, open ${site} once so Humblytics sees the first visit, then tell me it's done.

${rules}`;
}
